import {resolveScopedRoute, type CommandSearchContext} from '../components/panel-command-search';
import type {PanelAccessServices, PanelEntry, PanelEntryContext, PanelEntrySurface} from './types';

/** Converts command-search context to panel entry context. */
export const toPanelEntryContext = (ctx: CommandSearchContext): PanelEntryContext => ({
    scope: ctx.scope,
    scopePath: ctx.scopePath,
    services: ctx.services,
});

/** Returns false when an entry is hidden or fails access checks. */
export const isPanelEntryVisible = (entry: PanelEntry, ctx: PanelEntryContext): boolean => {
    if (typeof entry.hide === 'function' ? entry.hide(ctx) : entry.hide) {
        return false;
    }

    if (entry.canAccess && !entry.canAccess(ctx)) {
        return false;
    }

    if (entry.accessKey) {
        const access = ctx.services as PanelAccessServices | undefined;
        if (access?.canAccess && !access.canAccess(entry.accessKey)) {
            return false;
        }
    }

    return true;
};

/** Resolves the navigation/action target for an entry. */
export const resolvePanelEntryTarget = (
    entry: PanelEntry,
    ctx: PanelEntryContext,
): string | undefined => {
    if (entry.resolveTo) {
        return entry.resolveTo(ctx);
    }

    if (entry.routeTemplate && entry.scopeType) {
        return resolveScopedRoute(entry.routeTemplate, ctx.scope, entry.scopeType);
    }

    return entry.to;
};

/** Flattens nested entries for command search (leaves only). */
export const flattenPanelEntries = (
    entries: PanelEntry[],
    ctx: PanelEntryContext,
    surface?: PanelEntrySurface,
): PanelEntry[] => {
    const flat: PanelEntry[] = [];

    const walk = (items: PanelEntry[]) => {
        items.forEach((entry) => {
            if (surface && !entry.surfaces.includes(surface)) {
                if (entry.children?.length) {
                    walk(entry.children);
                }
                return;
            }

            if (!isPanelEntryVisible(entry, ctx)) {
                return;
            }

            if (entry.children?.length) {
                walk(entry.children);
                return;
            }

            flat.push(entry);
        });
    };

    walk(entries);
    return flat;
};

/**
 * Walks the entry tree for sidebar projection, preserving nesting.
 * Parents without the surface are still walked so children can appear.
 */
export const walkPanelEntriesForSidebar = (
    entries: PanelEntry[],
    ctx: PanelEntryContext,
): PanelEntry[] => {
    const result: PanelEntry[] = [];

    entries.forEach((entry) => {
        const hasSurface = entry.surfaces.includes('sidebar');
        const visible = isPanelEntryVisible(entry, ctx);

        if (entry.children?.length) {
            const children = walkPanelEntriesForSidebar(entry.children, ctx);
            if (!hasSurface || !visible) {
                result.push(...children);
                return;
            }
            if (!children.length && !entry.to && !entry.routeTemplate && !entry.resolveTo) {
                return;
            }
            result.push({...entry, children});
            return;
        }

        if (hasSurface && visible) {
            result.push(entry);
        }
    });

    return result;
};
